import { useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { AxiosError } from 'axios';

import useUnregisteredKit from 'services/kits/useUnregisteredKit';

import {
  ActivationStepper,
  ContainerSizes,
  PageContainer,
  Spinfinity,
  Typography,
} from 'components';
import { ActivationStep } from 'hooks/useActivationSteps';
import { PartnerLogo } from './PartnerLogo';
import {
  KitActivationState,
  PartnerKitStep,
  PartnerKitStepProps,
} from './types';

// Re-export types for backward compatibility
export type { KitActivationState, PartnerKitStep, PartnerKitStepProps };

// This flow is NOT gated behind `redesigned_kit_activation_flow`, unlike the
// authenticated /kits/activate flow. /partners/:kitId is anonymous, and
// feature flags are evaluated per main account (`fetchFeatures` returns `{}`
// without one — see packages/features/src/client.ts), so a flag here would
// read as "off" forever, even at 100% rollout. The redesign is always on for
// partners; the trade-off is that there's no kill-switch for this route.
//
// Only 2 visible stages regardless of how many PartnerKitStep entries exist:
// informationStep (index 0) is "Activate your kit"; surveyStep (index 1) is
// "Complete health forms" (which also flips "Activate" to a checkmark, since
// ActivationStepper marks anything before the current step done); finalStep
// (the last index) shows both as done via allCompleted. Same component the
// main authenticated flow uses, for a consistent look between the two.
const PARTNER_ACTIVATION_STEPS: ActivationStep[] = [
  { key: 'activate', label: 'Activate your kit' },
  { key: 'forms', label: 'Complete health forms' },
];

interface KitActivationForPartnersProps {
  steps: readonly PartnerKitStep[];
}

const KitActivationForPartners = ({ steps }: KitActivationForPartnersProps) => {
  const kitsMatch = useMatch<'kitId', string>('/partners/:kitId');
  const kitId = kitsMatch?.params.kitId;
  const { data: kit, error, isLoading, isSuccess } = useUnregisteredKit(kitId);

  const [currentActivationStep, setCurrentActivationStep] = useState(0);

  const [activationState, setActivationState] = useState<KitActivationState>({
    tinyAccount: { id: '' },
    kit: {},
  });

  const shouldSkipSurveys = !!(
    kit?.partner?.portal_config?.skip_surveys ||
    kit?.partner?.portal_config?.survey_type === 'skip'
  );

  useEffect(() => {
    const updateActivationState = () => {
      if (kit) {
        setActivationState(prevState => ({
          ...prevState,
          kit: kit,
          tinyAccount: kit.tinyaccount
            ? kit.tinyaccount
            : prevState.tinyAccount,
        }));
      }
    };

    const checkAndUpdateStep = () => {
      if (kit?.tinyaccount && !kit?.date_survey_completed) {
        setCurrentActivationStep(1);
      } else if (kit?.date_survey_completed && shouldSkipSurveys) {
        // Skip-survey partners mark the survey done at activation. The lookup
        // stops including the customer then, so route on the date instead.
        setCurrentActivationStep(2);
      }
    };
    updateActivationState();
    checkAndUpdateStep();
  }, [kit]);

  const StepComponent = steps[currentActivationStep]?.Component;

  const errorMessage =
    error instanceof AxiosError
      ? error.response?.data?.message || error.message
      : error?.message;

  return (
    <PageContainer
      size={ContainerSizes.SM}
      className='flex flex-column gap-2 p-4'
    >
      {!shouldSkipSurveys && (
        <ActivationStepper
          steps={PARTNER_ACTIVATION_STEPS}
          currentStepKey={currentActivationStep === 0 ? 'activate' : 'forms'}
          allCompleted={currentActivationStep === steps.length - 1}
        />
      )}
      {currentActivationStep !== 1 && (
        <div className='mt-4'>
          <PartnerLogo url={kit?.partner?.logo} />
        </div>
      )}
      {isLoading && <Spinfinity />}
      {(error ||
        (isSuccess &&
          !kit?.partner?.portal_config?.disable_kit_activation)) && (
        <div className='flex flex-column align-items-center gap-4 mt-4'>
          <Typography variant='body-s' className='red text-center'>
            {errorMessage ||
              'There is an error with your kit, please contact support at hello@tinyhealth.com'}
          </Typography>
        </div>
      )}
      {isSuccess && !!kit?.partner?.portal_config?.disable_kit_activation && (
        <div className='max-w-100'>
          {StepComponent && (
            <StepComponent
              step={currentActivationStep}
              setCurrentActivationStep={setCurrentActivationStep}
              activationState={activationState}
              setActivationState={setActivationState}
            />
          )}
        </div>
      )}
    </PageContainer>
  );
};

export default KitActivationForPartners;
